import React, { Fragment, useEffect, useState, useRef } from 'react'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'
import {
	Button,
	TextInput,
	TextInputMask,
	Form,
	FlexgridItem,
	Flexgrid,
	ActivityMonitor,
	IconBack,
} from '@wf-wfria/pioneer-core'
import { SystemMessage } from './SystemMessages'
import { RegistrationForm } from './RegistrationForm'
import { ShowLanguageUserSignOn } from './ShowLanguageUserSignOn'
import {
	DOCEX,
	WFORMS,
	RFI,
	ONBOARD,
	SBA,
	RICOH,
	DLO,
	COBO,
	EASYCONNECT,
	WFINFOLEASE,
	PLINFOLEASE,
	FORDPRO,
} from '../businessSegmentBrandIds'
import { formatContractNumber } from '../isValidField'

export function WcaForm({
	mode,
	inputFields,
	emailValid,
	showEsignContent,
	showTermsAndPolicies,
	customerOrContract,
	contractOrInvoice,
	invoiceOrSerial,
	registrationMessage,
	signOnButtonDisabled,
	signOnMessage,
	focusedElement,
	onlineHelpUrl,
	intl,
	signOnLocaleUrl,
	hideFieldErrors,
	showLanguageDropDown,
	onAuthenticateUser,
	onForgotPassword,
	onForgotUserId,
	onGetEmailConfirmation,
	onUpdateInput,
	onFocusInput,
	onHideAllMessages,
	onSelectCustomerOrContract,
	onSelectContractOrInvoice,
	onSelectInvoiceOrSerial,
	onCustomTextInputValidator,
	onShowSignOnErrorMessage,
	onSetHideFieldErrors,
}) {
	const formRef = useRef()
	const { formatMessage } = intl
	const valueMissing = formatMessage({ id: 'required-field.empty.error.label' })
	const [date, setDate] = useState(Date.now())
	const [showRegistration, setShowRegistration] = useState(false)
	const queryParams = new URLSearchParams(window.location.search)
	const localeIsFr = queryParams.get('locale') === 'fr'
	const usernameLocked = queryParams.get('username_locked')
	const usernameUnRegistered = queryParams.get('username_unregistered')
	const userNotfound = queryParams.get('user_notfound')

	useEffect(() => {
		if (
			usernameLocked !== null ||
			usernameUnRegistered !== null ||
			userNotfound !== null
		) {
			const warningMessage =
				mode === PLINFOLEASE || mode === FORDPRO
					? 'app-session-username-pl.warning.message'
					: mode === RICOH
						? 'app-session-username-ricoh.warning.message'
						: 'app-session-username-wf.warning.message'
			onShowSignOnErrorMessage(warningMessage)
		}
	}, [
		usernameLocked,
		usernameUnRegistered,
		userNotfound,
		onShowSignOnErrorMessage,
		mode,
	])

	useEffect(() => {
		if (
			(focusedElement !== 'loginUserId' && focusedElement !== 'password') ||
			customerOrContract !== '' ||
			contractOrInvoice !== '' ||
			invoiceOrSerial !== ''
		) {
			const newKey = Date.now()
			setDate(newKey)
		}
	}, [
		focusedElement,
		customerOrContract,
		contractOrInvoice,
		invoiceOrSerial,
		setDate,
	])

	useEffect(() => {
		if (!formRef.current) return
		if (hideFieldErrors.loginUserId || hideFieldErrors.password) {
			if (formRef.current?.resetField) {
				formRef.current.resetField('loginUserId')
				formRef.current.resetField('password')
			}
			const resetHideFieldErrors = {
				loginUserId: false,
				password: false,
			}
			onSetHideFieldErrors(resetHideFieldErrors)
		}
	}, [onSetHideFieldErrors, hideFieldErrors])

	const {
		loginUserId,
		password,
		email,
		existingEmail,
		dob,
		customerNumber,
		customerNumberRicoh,
		contractNumber,
		contractNumberRicoh,
		invoiceNumber,
		serialNumber,
	} = inputFields
	const registrationMessageId = registrationMessage.messageId
	const defaultInputClasses = 'textField'
	const emailInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(email),
	})
	const existingEmailInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(existingEmail),
	})
	const customerNumberInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(customerNumber),
	})
	const contractNumberInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(customerNumber),
	})
	const customerNumberRicohInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(customerNumberRicoh),
	})
	const contractNumberRicohInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(contractNumberRicoh),
	})
	const invoiceNumberInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(invoiceNumber),
	})
	const serialNumberInputClasses = classNames(defaultInputClasses, {
		HasInputViolation: hasViolations(serialNumber),
	})

	const instructionId = getInstructionId(mode)
	const brandIdIsCCOrRFIOrOnboardingOrLendMgmt =
		mode === DOCEX ||
		mode === WFORMS ||
		mode === RFI ||
		mode === ONBOARD ||
		mode === SBA ||
		mode === DLO ||
		mode === COBO ||
		mode === EASYCONNECT
	const signOnHelpClasses = classNames({
		hide: brandIdIsCCOrRFIOrOnboardingOrLendMgmt,
	})
	function isEmpty(str) {
		return str.value.length === 0
	}

	function hasViolations(str) {
		return str?.errorIds.length > 0
	}

	function validate(errorSchema) {
		const errors = {}
		for (let [id, validators] of Object.entries(errorSchema)) {
			for (let i = 0; i < validators.length; i++) {
				if (validators[i]) {
					errors[id] = true
				}
			}
		}
		return errors
	}

	function registrationButtonDisabled() {
		if (
			mode !== DOCEX &&
			mode !== WFORMS &&
			mode !== ONBOARD &&
			mode !== SBA &&
			mode !== RFI &&
			mode !== DLO &&
			mode !== COBO &&
			mode !== EASYCONNECT
		) {
			const errorSchema = {
				customerNumber: [hasViolations(customerNumber)],
				contractNumber: [hasViolations(contractNumber)],
				serialNumber: [hasViolations(serialNumber)],
				invoiceNumber: [hasViolations(invoiceNumber)],
				contractNumberRicoh: [hasViolations(contractNumberRicoh)],
				customerNumberRicoh: [hasViolations(customerNumberRicoh)],
			}
			const errors = validate(errorSchema)
			if (Object.keys(errors).length !== 0) {
				return true
			}
			if (mode !== RICOH) {
				const violations = [
					isEmpty(customerNumber) && isEmpty(contractNumber),
					isEmpty(email),
					isEmpty(invoiceNumber) && isEmpty(serialNumber),
				]
				return violations.filter((isEmpty) => isEmpty).length > 0
			} else {
				const contractOrInvoiceEmpty =
					isEmpty(contractNumberRicoh) && isEmpty(invoiceNumber)
				return isEmpty(customerNumberRicoh) || contractOrInvoiceEmpty
			}
		}
		return hasViolations(email) || isEmpty(email)
	}

	function getInstructionId(mode) {
		return `wca-form.new-user-registration.${mode}.instruction`
	}

	const registrationMessageIdIsArray = Array.isArray(registrationMessageId)
	if (
		(!registrationMessageIdIsArray &&
			registrationMessageId &&
			registrationMessageId.search('api-service-error') === -1 &&
			!hasViolations(email)) ||
		(registrationMessageIdIsArray && registrationMessageId.length === 0)
	) {
		onHideAllMessages()
	}

	function onFocusFactory(focusedForm) {
		return (event) => {
			const focusedElement = event.target.id
			onFocusInput(focusedElement, focusedForm)
		}
	}

	function onChange({ id, value }) {
		if (id === 'userEmail') {
			onUpdateInput('email', value)
		} else if (id === 'existingUserEmail') {
			onUpdateInput('existingEmail', value)
		} else if (id === 'contractNumber') {
			const formattedValue = formatContractNumber(value)
			onUpdateInput(id, formattedValue)
		} else {
			onUpdateInput(id, value)
		}
	}

	function onEnterKeyDown(event) {
		if (event.keyCode === 13 && !signOnButtonDisabled) {
			onAuthenticateUser()
		}
	}
	function clickSignon() {
		ActivityMonitor.resetTrackingActivity()
		onSetHideFieldErrors({ loginUserId: true, password: true })
		if (!signOnButtonDisabled) {
			onAuthenticateUser()
		}
		if (signOnButtonDisabled) {
			onShowSignOnErrorMessage('app-invalid-sign-on')
		}
		const input = document.getElementById('loginUserId')
		if (input) {
			input.focus()
		}
	}
	function onUserSignOnHere(signOnLocaleUrl) {
		window.location.replace(signOnLocaleUrl)
	}

	function onTextInputValidator(elementId, inputValue) {
		return onCustomTextInputValidator(elementId, inputValue, formatMessage)
	}

	if (showEsignContent || showTermsAndPolicies) {
		return <Fragment />
	}
	return (
		<div className="MyAccountsLoginForm">
			<Flexgrid columnGap={6} rowGap={2} direction="row">
				{!showRegistration ? (
					<>
						{/* Login Section */}
						<FlexgridItem span={{ xxs: 12, sm: 12, md: 12, lg: 12 }}>
							<Form action="#" ref={formRef} onSubmit={clickSignon}>
								<div className="LoginBox LoginBoxShifted">
									<HeaderText mode={mode} />
									<Flexgrid direction="column" rowGap={1}>
										<FlexgridItem span={12}>
											<SystemMessage
												className="SignOnMessage"
												show={signOnMessage.show}
												iconKind={signOnMessage.iconKind}
												messageId={signOnMessage.messageId}
											/>
										</FlexgridItem>
										<Flexgrid rowGap={2}>
											<FlexgridItem span={12}>
												<TextInput
													aria-label={formatMessage({
														id: 'wca-form.user-id.aria-label',
													})}
													label={formatMessage({
														id: 'wca-form.user-id.label',
													})}
													aria-required
													required={true}
													id="loginUserId"
													name="loginUserId"
													type="text"
													size="27"
													value={loginUserId.value}
													onValueChange={onChange}
													onFocus={onFocusFactory('WcaLogin')}
													nativeValidationMessages={{ valueMissing }}
												/>
											</FlexgridItem>

											<FlexgridItem span={12}>
												<TextInputMask
													aria-label={formatMessage({
														id: 'wca-form.password.aria-label',
													})}
													label={formatMessage({
														id: 'wca-form.password.label',
													})}
													aria-required
													required={true}
													id="password"
													name="password"
													type="password"
													size="27"
													value={password.value}
													onValueChange={onChange}
													onFocus={onFocusFactory('WcaLogin')}
													onKeyDown={onEnterKeyDown}
													nativeValidationMessages={{ valueMissing }}
												/>
											</FlexgridItem>

											<FlexgridItem span={12}>
												<Button
													type="submit"
													className="SignOnButton"
													kind="primary"
												>
													<FormattedMessage id="wca-form.button.sign-on.label" />
												</Button>
											</FlexgridItem>
										</Flexgrid>
										<Flexgrid
											className="SignOnLinkRow"
											alignHorizontal="start"
											alignVertical="center"
											columnGap={2}
											style={{ marginTop: '12px' }}
										>
											<FlexgridItem span="auto">
												<Button
													kind="chromeless"
													className="ForgotPasswordButton"
													id="ForgotPasswordButton"
													onClick={onForgotPassword}
													tabIndex="0"
												>
													<FormattedMessage id="wca-form.button.forgot-password.label" />
												</Button>
											</FlexgridItem>

											<FlexgridItem
												span="auto"
												className="SignOnHelpFlexgridItem"
											>
												<SignOn
													show={!localeIsFr}
													signOnHelpClasses={signOnHelpClasses}
													onlineHelpUrl={onlineHelpUrl}
												/>
											</FlexgridItem>

											<FlexgridItem span="auto">
												<Button
													kind="chromeless"
													className="ForgotUserIdButton"
													id="ForgotUserIdButton"
													onClick={onForgotUserId}
													onFocus={onFocusInput}
													tabIndex="0"
												>
													<FormattedMessage id="wca-form.button.forgot-user-id.label" />
												</Button>
											</FlexgridItem>
										</Flexgrid>

										{localeIsFr && (
											<FlexgridItem span={12} style={{ marginTop: '6px' }}>
												<SignOn
													show={true}
													signOnHelpClasses={signOnHelpClasses}
													onlineHelpUrl={onlineHelpUrl}
												/>
											</FlexgridItem>
										)}

										{(mode === PLINFOLEASE || mode === WFINFOLEASE) && (
											<FlexgridItem
												span={12}
												style={{ marginTop: '12px', marginBottom: '8px' }}
											>
												<ShowLanguageUserSignOn
													mode={mode}
													formatMessage={formatMessage}
													onUserSignOnHere={onUserSignOnHere}
													signOnLocaleUrl={signOnLocaleUrl}
													showLanguageDropDown={showLanguageDropDown}
												/>
											</FlexgridItem>
										)}

										<FlexgridItem span="auto">
											<Button
												className="NewUserButton"
												fullWidth
												kind="standard"
												onClick={() => setShowRegistration(true)}
											>
												<FormattedMessage id="wca-form.link.new-user.label" />
											</Button>
										</FlexgridItem>
									</Flexgrid>
								</div>
							</Form>
						</FlexgridItem>
					</>
				) : (
					<>
						{/* Registration Section */}
						<FlexgridItem span={{ xxs: 12, sm: 12, md: 12, lg: 12 }}>
							<div className="RegistrationBox">
								<Flexgrid
									alignHorizontal="spaceBetween"
									alignVertical="center"
									wrap="wrap"
									rowGap={1}
								>
									<FlexgridItem span={12}>
										<Button
											kind="chromeless"
											onClick={() => setShowRegistration(false)}
											leftIcon={<IconBack size="xsmall" decorative />}
											style={{ padding: 0, marginBottom: '16px' }}
										>
											Back to login
										</Button>
									</FlexgridItem>
									<FlexgridItem span={12}>
										<p className="NewUserRegistrationText">
											<FormattedMessage id="wca-form.new-user-registration.form-header.text" />
										</p>
									</FlexgridItem>

									<FlexgridItem span="auto">
										<span className="FieldHint">
											<FormattedMessage
												id={instructionId}
												defaultMessage="{defaultValue}"
												values={{
													defaultValue: <FormattedMessage id={instructionId} />,
												}}
											/>
										</span>
									</FlexgridItem>
								</Flexgrid>

								<Flexgrid
									direction="column"
									rowGap={1}
									style={{ marginTop: '16px' }}
								>
									<RegistrationForm
										focusedElement={focusedElement}
										mode={mode}
										formatMessage={formatMessage}
										email={email}
										existingEmail={existingEmail}
										dob={dob}
										customerNumber={customerNumber}
										customerNumberRicoh={customerNumberRicoh}
										contractNumber={contractNumber}
										contractNumberRicoh={contractNumberRicoh}
										invoiceNumber={invoiceNumber}
										serialNumber={serialNumber}
										customerOrContract={customerOrContract}
										contractOrInvoice={contractOrInvoice}
										invoiceOrSerial={invoiceOrSerial}
										registrationMessage={registrationMessage}
										customerNumberInputClasses={customerNumberInputClasses}
										contractNumberInputClasses={contractNumberInputClasses}
										customerNumberRicohInputClasses={
											customerNumberRicohInputClasses
										}
										contractNumberRicohInputClasses={
											contractNumberRicohInputClasses
										}
										invoiceNumberInputClasses={invoiceNumberInputClasses}
										serialNumberInputClasses={serialNumberInputClasses}
										emailValid={emailValid}
										showEsignContent={showEsignContent}
										showTermsAndPolicies={showTermsAndPolicies}
										registrationButtonDisabled={registrationButtonDisabled()}
										emailInputClasses={emailInputClasses}
										existingEmailInputClasses={existingEmailInputClasses}
										hideFieldErrors={hideFieldErrors}
										onSetHideFieldErrors={onSetHideFieldErrors}
										onChange={onChange}
										onUpdateInput={onUpdateInput}
										onFocusFactory={onFocusFactory}
										onSelectCustomerOrContract={onSelectCustomerOrContract}
										onSelectContractOrInvoice={onSelectContractOrInvoice}
										onSelectInvoiceOrSerial={onSelectInvoiceOrSerial}
										onGetEmailConfirmation={onGetEmailConfirmation}
										onTextInputValidator={onTextInputValidator}
									/>
								</Flexgrid>
							</div>
						</FlexgridItem>
					</>
				)}
				<hr />
				<FlexgridItem>
					<Footer mode={mode} />
				</FlexgridItem>
			</Flexgrid>
		</div>
	)
}

export function HeaderText({ mode }) {
	return (
		<>
			<h3 className="wf-col form-header WBLoginHeaderText">
				<FormattedMessage id="wca-form.sign-on.form-header.label" />
			</h3>
			<div className="WBLoginSubtext">
				<FormattedMessage id="wca-form.sign-on.form-header.label-wb-subtext" />
			</div>
		</>
	)
}

export function Footer({ mode }) {
	if (mode === EASYCONNECT) {
		return (
			<div className="wf-col--12 FooterWrapper">
				<div className="wf-col--mob-only-12 wf-col--tab-12 wf-col--desk-10 WrapperText wf-text7">
					<div className="wf-text7 InvestmentText">
						<FormattedMessage id="footer.investment-and-insurance-products.label" />
					</div>
					<ul className="wf-list-styled">
						<li className="wf-text7">
							<FormattedMessage id="footer.investment.not-insured.reason.1" />
						</li>
						<li className="wf-text7">
							<FormattedMessage id="footer.investment.not-deposit.reason.2" />
						</li>
						<li className="wf-text7">
							<FormattedMessage id="footer.investment.subject-to-investment-risks.reason.3" />
						</li>
					</ul>
				</div>
				<div className="DisclosureText wf-text7">
					<span>
						<FormattedMessage id="footer.deposit.products-disclosure" />
					</span>
				</div>
			</div>
		)
	}
	return null
}

export function SignOn({ show, signOnHelpClasses, onlineHelpUrl }) {
	if (show) {
		return (
			<div className={signOnHelpClasses}>
				<a
					href={onlineHelpUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="wf-button wf-button--simple SignOnHelpUrl wf-u-link-export"
					key="SignOnHelpUrl"
					tabIndex="0"
				>
					<FormattedMessage id="wca-form.link.sign-on-help.label" />
					<span className="wf-u-visually-hidden">
						<FormattedMessage id="wca-form.link.additional-aria-text.text" />
					</span>
				</a>
			</div>
		)
	}
	return <Fragment />
}
